import { SenderDomainStatus, type SenderDomainView } from '@today/types'
import { Badge, Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/**
 * Props for {@link SenderDomainPanel}.
 *
 * @property domain - The from-address domain to verify.
 * @property record - The verification record, or null before the first Verify.
 * @property fallbackAddress - Set while email goes out from this platform address instead.
 * @property onVerify - Registers or re-checks the domain.
 * @property onCopy - Copies one record value.
 * @property isDisabled - Read-only access.
 * @property isBusy - A verification is in flight.
 */
export interface SenderDomainPanelProps {
	domain: string
	record: SenderDomainView | null
	fallbackAddress: string | null
	onVerify: () => void
	onCopy: (value: string) => void
	isDisabled?: boolean
	isBusy?: boolean
}

const STATUS: Record<SenderDomainStatus, { text: string; variant: 'success' | 'muted' | 'danger' }> = {
	[SenderDomainStatus.VERIFIED]: { text: 'Verified', variant: 'success' },
	[SenderDomainStatus.PENDING]: { text: 'Pending', variant: 'muted' },
	[SenderDomainStatus.FAILED]: { text: 'Failed', variant: 'danger' },
}

/**
 * Own sending-domain verification (TO-382): the fallback warning, the status,
 * one copy block per DNS record (DKIM, SPF, DMARC) and the Verify poll. Pure.
 */
export function SenderDomainPanel({
	domain,
	record,
	fallbackAddress,
	onVerify,
	onCopy,
	isDisabled = false,
	isBusy = false,
}: SenderDomainPanelProps): ReactElement {
	const status = record ? STATUS[record.status] : null
	return (
		<section aria-label="Sending domain" className="flex flex-col gap-3">
			{fallbackAddress ? (
				<p role="status" className="rounded-ds-md border border-ds-border bg-ds-surface-muted p-3 text-ds-sm">
					{`Emails are sent from ${fallbackAddress} until ${domain} is verified.`}
				</p>
			) : null}
			<div className="flex items-center gap-2">
				<span className="text-ds-base">{domain}</span>
				{status ? (
					<Badge variant={status.variant} size="sm">
						{status.text}
					</Badge>
				) : null}
				<Button variant="secondary" size="sm" isDisabled={isDisabled || isBusy} onPress={onVerify}>
					Verify
				</Button>
			</div>
			{record ? (
				<ul className="flex flex-col gap-2">
					{record.dnsRecords.map((dns) => (
						<li
							key={`${dns.type}:${dns.name}`}
							aria-label={`DNS record ${dns.purpose} ${dns.name}`}
							className="flex flex-col gap-1 rounded-ds-md border border-ds-border p-2 text-ds-sm"
						>
							<span className="text-ds-text-muted">{`${dns.purpose} · ${dns.type}`}</span>
							<code>{dns.name}</code>
							<code>{dns.value}</code>
							<Button variant="muted" size="sm" aria-label={`Copy ${dns.name}`} onPress={() => onCopy(dns.value)}>
								Copy
							</Button>
						</li>
					))}
				</ul>
			) : null}
		</section>
	)
}
